// src/components/UpgradeModal.tsx

import React from "react";
import "../styles/UpgradeModal.css";

type Props = {
  open: boolean;
  onClose: () => void;
  onCheckout: (plan: "monthly" | "lifetime") => void;
  plan: "free" | "premium";
};

const PRICES = {
  monthly: 7,
  lifetime: 115,
};

const features = [
  "AI cover letter & bullet rewrites",
  "Saved jobs & profiles",
  "Export DOCX/PDF",
  "Multi-country search",
  "Unlimited searches & analyses",
];

export default function UpgradeModal({ open, onClose, onCheckout, plan }: Props) {
  if (!open) return null;

  return (
    // 🔹 Outer overlay: allow vertical scroll on small screens
    <div className="upgrade-modal-overlay fixed inset-0 z-50 flex justify-center p-4 overflow-y-auto">
      {/* this extra div gives a bit of top/bottom margin when scrolling */}
      <div className="flex w-full max-w-5xl items-start justify-center my-6">
        {/* 🔹 Card: limit height & allow internal scroll if needed */}
        <div className="upgrade-root w-full rounded-2xl bg-bg text-text shadow-2xl border border-border max-h-[90vh] overflow-y-auto">
          {/* header */}
          <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-bg z-10">
            <h2 className="text-xl font-semibold">Upgrade to Premium</h2>
            <button
              onClick={onClose}
              className="h-8 w-8 grid place-items-center rounded-md hover:bg-panel"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* cards */}
          <div className="upgrade-modal__body">
            {plan === "premium" && (
              <div className="upgrade-modal__current-plan">
                <div>
                  <span>Current Plan</span>
                  <strong>JobTailor Premium</strong>
                </div>

                <span className="upgrade-modal__active-badge">
                  Active
                </span>
              </div>
            )}

            <div className="upgrade-pricing-grid">

              {/* FREE */}
              <article className="upgrade-plan-card">
                <div className="upgrade-plan-card__top">
                  <div className="upgrade-plan-card__label">
                    FREE
                  </div>

                  <div className="upgrade-plan-card__price">
                    <strong>$0</strong>
                  </div>

                  <p className="upgrade-plan-card__description">
                    Get started with JobTailor's core career tools.
                  </p>
                </div>

                <ul className="upgrade-plan-card__features">
                  <li>
                    <span>✓</span>
                    Limited job searches
                  </li>

                  <li>
                    <span>✓</span>
                    Limited job analyses
                  </li>

                  <li>
                    <span>✓</span>
                    Build professional CVs
                  </li>

                  <li>
                    <span>✓</span>
                    Export DOCX/PDF
                  </li>

                  <li>
                    <span>✓</span>
                    Multi-country job search
                  </li>
                </ul>

                <button
                  type="button"
                  className="upgrade-plan-card__button upgrade-plan-card__button--secondary"
                  onClick={onClose}
                >
                  Keep Free
                </button>
              </article>

              {/* MONTHLY */}
              <article className="upgrade-plan-card upgrade-plan-card--featured">
                <div className="upgrade-plan-card__badge">
                  MOST POPULAR ⭐
                </div>

                <div className="upgrade-plan-card__top">
                  <div className="upgrade-plan-card__label upgrade-plan-card__label--purple">
                    MONTHLY
                  </div>

                  <div className="upgrade-plan-card__price">
                    <strong>${PRICES.monthly}</strong>
                    <span>/mo</span>
                  </div>

                  <p className="upgrade-plan-card__description">
                    Full access with the flexibility of monthly billing.
                  </p>
                </div>

                <ul className="upgrade-plan-card__features">
                  {features.map((feature) => (
                    <li key={feature}>
                      <span>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  disabled={plan === "premium"}
                  className="upgrade-plan-card__button upgrade-plan-card__button--primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (plan === "premium") return;

                    void onCheckout("monthly");
                  }}
                >
                  {plan === "premium" ? "✓ Current Plan" : "Continue"}
                </button>

                <p className="upgrade-plan-card__footer">
                  Cancel anytime. No hidden fees.
                </p>
              </article>

              {/* LIFETIME */}
              <article className="upgrade-plan-card upgrade-plan-card--lifetime">
                <div className="upgrade-plan-card__badge upgrade-plan-card__badge--lifetime">
                  BEST VALUE 👑
                </div>

                <div className="upgrade-plan-card__top">
                  <div className="upgrade-plan-card__label upgrade-plan-card__label--purple">
                    LIFETIME
                  </div>

                  <div className="upgrade-plan-card__price">
                    <strong>${PRICES.lifetime}</strong>
                    <span>one-time</span>
                  </div>

                  <p className="upgrade-plan-card__description">
                    Pay once and keep Premium access.
                  </p>
                </div>

                <ul className="upgrade-plan-card__features">
                  {features.map((feature) => (
                    <li key={feature}>
                      <span>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  disabled={plan === "premium"}
                  className="upgrade-plan-card__button upgrade-plan-card__button--primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (plan === "premium") return;

                    void onCheckout("lifetime");
                  }}
                >
                  {plan === "premium" ? "✓ Current Plan" : "Continue"}
                </button>

                <p className="upgrade-plan-card__footer">
                  Pay once. Updates included.
                </p>
              </article>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
}
